import { UserAvailability } from './../models/UserAvailability';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { User } from '../models/User';

@Injectable({
  providedIn: 'root'
})
export class UserAvailabilityService {
  private apiBaseUrl = environment.apiBaseUrl;

  public organizationAvailability =
    new BehaviorSubject<Array<UserAvailability> | null>(null);
  public organizationAvailabilityObservable$ =
    this.organizationAvailability.asObservable();

  private static handleError(error: Response | any): Observable<any> {
    console.error('ApiService::handleError', error);
    return throwError(error);
  }

  constructor(private http: HttpClient) {}

  getAvailabilityByUserId(userId: string): Observable<Array<UserAvailability>> {
    const url = `${this.apiBaseUrl}/availability/${userId}`;
    return this.http
      .get<any>(url)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  getAvailableUsersFull(searchCriteria: any): Observable<Array<User>> {
    const url = `${this.apiBaseUrl}/available/users/full`;
    return this.http
      .post<Array<User>>(url, searchCriteria)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  getAvailableUsers(): Observable<Array<string>> {
    const url = `${this.apiBaseUrl}/available/users`;
    return this.http
      .get<Array<string>>(url)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  searchAvailableUsers(searchTerms: any): Observable<Array<string>> {
    const url = `${this.apiBaseUrl}/available/users`;
    return this.http
      .post<UserAvailability>(url, searchTerms)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  getUserAvailabilityByDates(
    availability: any
  ): Observable<Array<UserAvailability>> {
    const url = `${this.apiBaseUrl}/profile/availability`;
    return this.http
      .post<any>(url, availability)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  saveUserAvailability(availability: any): Observable<UserAvailability> {
    const url = `${this.apiBaseUrl}/availability`;
    return this.http
      .post<UserAvailability>(url, availability)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  copyUserAvailability(availability: any): Observable<Array<UserAvailability>> {
    const url = `${this.apiBaseUrl}/availability/copy`;
    return this.http
      .post<any>(url, availability)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  /**
   * Save a batch of new availability periods (expanded from a date range +
   * weekdays + breaks). If any period overlaps existing presence and `overwrite`
   * is not set, the server changes nothing and returns needsConfirm=true so the
   * caller can confirm replacement. With overwrite=true the overlapping existing
   * rows are replaced. Resolves to { added, replaced, conflicts, needsConfirm }.
   */
  saveUserAvailabilityBatch(payload: {
    availabilities: Array<any>;
    overwrite?: boolean;
  }): Observable<{
    added: number;
    replaced: number;
    conflicts: number;
    needsConfirm: boolean;
    disallowed?: boolean;
  }> {
    const url = `${this.apiBaseUrl}/availability/batch`;
    return this.http
      .post<any>(url, payload)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  deleteUserAvailability(id: string): Observable<any> {
    const url = `${this.apiBaseUrl}/availability/${id}`;
    return this.http
      .delete<any>(url)
      .pipe(catchError(UserAvailabilityService.handleError));
  }

  setOrganizationAvailability(
    availability: Array<UserAvailability> | null
  ): void {
    this.organizationAvailability.next(availability);
  }

  getOrganizationAvailability(): Array<UserAvailability> | null {
    return this.organizationAvailability.value;
  }
}
